import { Chart } from "chart.js";
import { datalabelFont, legendConfig, legendPad, onResize, tooltipTitleFont } from "./chartJsPlugins";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { infoBubble } from "../../helpers/infoBubble";
import { fillMissingData, PsatMetricSampleRow, upperizeStringify } from "./chartUtils";
import { htmlLegendPlugin, generateChartId } from './configs';

// @ts-ignore
window.initComplexBlock = (function ($, blockId, config, schoolResponse, networkResponse, districtResponse, bubbleConfigs, notUseDefaultTooltip) {
    
    const getTooltip = (key) => {
        return bubbleConfigs[key] ?? '';
    }
    const getCategoryMappedTooltip = (category) => {
        const mappedField = mappedBubbleConfigFieldKeys[category];
        return getTooltip(mappedField);
    }
    // @TODO create new keys for these
    const mappedBubbleConfigFieldKeys =
    {
        "ALL": "allstudents",
        "Economic Disadvantage": "economicdisadvantage",
        "English Learner Status": "englishlearner",
        "Gender": "gender",
        "IEP Status": "iep",
        "Race/Ethnicity": "raceethnicity",
        "Temporary Living Situation Status": "stls",
    }
    const scopedId = (id: string) => `${blockId}-${id}`;
    const parentScope = '.' + scopedId('metric-block');

    const tempShaper = (response) => {
        const data = [];
        for (let year of response.SchoolYears) {
            data.push(...year.Categories.map(category => ({
                ...upperizeStringify(category),
                CATEGORY_DESCRIPTION: category.CategoryDescription,
                SCHOOL_YEAR: year.SchoolYear,

                LESSTHAN10: category.Lessthan10 == "" ? 0 : 1,
                NODATA: category.NoData == "" ? 0 : 1,

                NETWORK: response.Network,

                NOTYETAPPROACHING_NUMERATOR: category.NotyetApproachingNumerator,
                NOTYETAPPROACHING_PERCENT: category.NotyetApproachingPercent,

                APPROACHING_NUMERATOR: category.ApproachingNumerator,
                APPROACHING_PERCENT: category.ApproachingPercent,

                MEETSEXCEEDS_NUMERATOR: category.MeetExceedsNumerator,
                MEETSEXCEEDS_PERCENT: category.MeetExceedsPercent,

                METRIC_TITLE: response.MetricTitle

            })))
        }
        return data.sort((a, b) => a.CATEGORY_DESCRIPTION.localeCompare(b.CATEGORY_DESCRIPTION))
            .sort((a, b) => a.SCHOOL_YEAR.localeCompare(b.SCHOOL_YEAR));
    }

    const schoolData = tempShaper(fillMissingData(schoolResponse, PsatMetricSampleRow));
    const networkData = tempShaper(fillMissingData(networkResponse, PsatMetricSampleRow));
    const districtData = tempShaper(fillMissingData(districtResponse, PsatMetricSampleRow));

    // @TODO finish update these to come from epi
    let network = config.network;
    let schoolName = config.schoolName;
    let chartMetric = districtResponse.MetricTitle;
    let currentYear = '2023-2024';
    let trendLabels = districtResponse.SchoolYears.map(y => y.SchoolYear).sort();
    let selectedCategory = "ALL";
    const yearSort = (a, b) => (trendLabels.indexOf(a.SCHOOL_YEAR) - trendLabels.indexOf(b.SCHOOL_YEAR));

    let snapshotLabels = {
        "ALL": getLabels("ALL"),
        "Race/Ethnicity": getLabels("Race/Ethnicity"),
        "Economic Disadvantage": getLabels("Economic Disadvantage"),
        "English Learner Status": getLabels("English Learner Status"),
        "Gender": getLabels("Gender"),
        "IEP Status": getLabels("IEP Status"),
        "Temporary Living Situation Status": getLabels("Temporary Living Situation Status")
    }

    const snapshotTooltipConfig = {
        enabled: true, titleFont: tooltipTitleFont, callbacks: {
            title: function (tooltipItem) {
                return ''
            }, label: function (tooltipItem) {
                let idArray = tooltipItem.chart.canvas.id.replace(scopedId(''), '').split("_");
                let desc = idArray.pop();
                let cat = idArray.pop();
                let raw = getSnapshotRaw(desc, cat);
                let data = raw[tooltipItem.dataIndex];
                let percent;
                let total;
                switch (tooltipItem.datasetIndex) {
                    case 0:
                        total = "NOTYETAPPROACHING_NUMERATOR";
                        percent = "NOTYETAPPROACHING_PERCENT";
                        break;
                    case 1:
                        total = "APPROACHING_NUMERATOR";
                        percent = "APPROACHING_PERCENT";
                        break
                    case 2:
                        total = "MEETSEXCEEDS_NUMERATOR";
                        percent = "MEETSEXCEEDS_PERCENT";

                }
                let line1 = [`${tooltipItem.label}- % ${tooltipItem.dataset.label}: ${data[percent]}%`];
                let line2 = `Total ${tooltipItem.dataset.label}: ${Number(data[total]).toLocaleString()}`;
                let line3 = `Total Students: ${Number(data.DENOMINATOR).toLocaleString()}`;
                line1.push(line2, line3);
                return line1;
            }
        }
    };

    const trendTooltipConfig = {
        enabled: true, titleFont: tooltipTitleFont, callbacks: {
            title: function (tooltipItem) {
                return ''
            }, label: function (tooltipItem) {
                let idArray = tooltipItem.chart.canvas.id.replace(scopedId(''), '').split("_");
                let desc = idArray.pop();
                let cat = idArray.pop();
                let dataset = idArray.pop();
                let raw = getTrendRaw(dataset, desc, cat);
                let data = raw[tooltipItem.dataIndex];
                let percent;
                let total;
                // pull figures from the correct column
                switch (tooltipItem.datasetIndex) {
                    case 0:
                        total = "NOTYETAPPROACHING_NUMERATOR";
                        percent = "NOTYETAPPROACHING_PERCENT";
                        break;
                    case 1:
                        total = "APPROACHING_NUMERATOR";
                        percent = "APPROACHING_PERCENT";
                        break
                    case 2:
                        total = "MEETSEXCEEDS_NUMERATOR";
                        percent = "MEETSEXCEEDS_PERCENT";

                }
                let line1 = [`${tooltipItem.label}- % ${tooltipItem.dataset.label}: ${data[percent]}%`];
                let line2 = `Total ${tooltipItem.dataset.label}: ${Number(data[total]).toLocaleString()}`;
                let line3 = `Total Students: ${Number(data.DENOMINATOR).toLocaleString()}`;
                line1.push(line2, line3);
                return line1;
            }
        }
    };


    const snapshotDatalabelConfig = {
        formatter: function (value, context) {
            let idArray = context.chart.canvas.id.replace(scopedId(''), '').split("_");
            let desc = idArray.pop();
            let cat = idArray.pop();
            let data = getSnapshotRaw(desc, cat);
            let label = Array.isArray(value) ? value[0] : value;
            let target;
            switch (context.datasetIndex) {
                case 0:
                    target = "NOTYETAPPROACHING_PERCENT";
                    break;
                case 1:
                    target = "APPROACHING_PERCENT";
                    break
                case 2:
                    target = "MEETSEXCEEDS_PERCENT"
            }
            if (label) {
                return data[context.dataIndex][target];
            } else {
                return 'Fewer than 10 students or No Data';
            }
        },
        display: function (ctx) {
            const scale = ctx.chart.scales.x;
            let value = ctx.dataset.data[ctx.dataIndex];
            // Get size of visible bar to determine if is room for a datalabel
            let num = Array.isArray(value) ? Math.abs(value[0]) - Math.abs(value[1]) : value;
            let range = Math.max(scale.max - scale.min, 1);
            if (isNaN(num)) {
                return true;
            }
            // Require a bit more room for Network and District, which will have longer labels.
            return (ctx.chart.width / range) * num > 20 + (2 * ctx.dataIndex);
        },
        color: '#000000',
        font: datalabelFont,
    };

    const trendDatalabelConfig = {
        formatter: function (value, context) {

            // Pull category, category description, and school/network/district from canvas id
            let idArray = context.chart.canvas.id.replace(scopedId(''), '').split("_");
            let desc = idArray.pop();
            let cat = idArray.pop();
            let dataset = idArray.pop();
            let data = getTrendRaw(dataset, desc, cat);
            let label = Array.isArray(value) ? value[0] : value;
            let target;
            switch (context.datasetIndex) {
                case 0:
                    target = "NOTYETAPPROACHING_PERCENT";
                    break;
                case 1:
                    target = "APPROACHING_PERCENT";
                    break
                case 2:
                    target = "MEETSEXCEEDS_PERCENT"
            }

            if (label) {
                return data[context.dataIndex][target];
            } else {
                return 'Fewer than 10 students or No Data';
            }
        },
        display: function (ctx) {
            const scale = ctx.chart.scales.x;
            let value = ctx.dataset.data[ctx.dataIndex];

            // Get size of visible bar to determine if there is room for a datalabel
            let num = Array.isArray(value) ? Math.abs(value[0]) - Math.abs(value[1]) : value;
            let range = Math.max(scale.max - scale.min, 1);
            let dataset = ctx.chart.canvas.id.replace(scopedId(''), '').split("_").at(-3);
            if (isNaN(num)) {
                return true;
            }
            if (dataset !== "school") {
                // Require a bit more room for Network and District, which will have longer labels.
                return (ctx.chart.width / range) * num > 20 + (2 * ctx.dataIndex);
            } else {
                return (ctx.chart.width / range) * num > 20;
            }
        },
        color: '#000000',
        font: datalabelFont,
    };

    $(parentScope).find(".sat-math-school-trends, .sat-math-district-trends, .sat-math-network-trends").hide().removeClass('hidden');

    function getLabels(type) {
        return Array.from(new Set(schoolData.filter((value) => value.CATEGORY === type)
            .map((item) => item.CATEGORY_DESCRIPTION))).sort()
    }

    createSnapshot();
    setTrendChart();
    setSnapshotHeight();


    let trendSelect = document.getElementById(scopedId("sat-math-trend-select"));
    trendSelect.addEventListener("change", showTrends);

    // This block is to get around the issue that a 'change' event wouldn't be triggered if the user clicks back from snapshot to the trend they were last viewing
    $(`#${scopedId("sat-math-trend-select")}`).bind('click', function () {
        let value = $(this).find('option:selected').val();
        let vals = ["school", "network", "district"];
        if (vals.includes(value)) {
            displayTrend(value);
        }
    });

    $(`#${scopedId("sat-math-show-snapshot")}`).on("click", showSnapshot);

    function getSnapshotRaw(category, label) {
        let schoolArr = schoolData.filter((value) => value.CATEGORY === category && value.CATEGORY_DESCRIPTION === label && value.METRIC_TITLE === chartMetric).filter((value) => value.SCHOOL_YEAR === currentYear);
        let networkArr = networkData.filter((value) => value.CATEGORY === category && value.CATEGORY_DESCRIPTION === label && value.METRIC_TITLE === chartMetric && value.NETWORK === network && snapshotLabels[category].includes(value.CATEGORY_DESCRIPTION)).filter((value) => value.SCHOOL_YEAR === currentYear);
        let districtArr = districtData.filter((value) => value.CATEGORY === category && value.CATEGORY_DESCRIPTION === label && value.METRIC_TITLE === chartMetric && snapshotLabels[category].includes(value.CATEGORY_DESCRIPTION)).filter((value) => value.SCHOOL_YEAR === currentYear);
        return [...schoolArr, ...networkArr, ...districtArr];
    }

    function getTrendRaw(data, category, label) {
        let dataset;

        switch (data) {
            case "school":
                dataset = schoolData;
                break;
            case "network":
                dataset = networkData.filter((value) => value.NETWORK === network);
                break;
            case "district":
                dataset = districtData;
                break;
        }
        
        return dataset.filter((value) => value.CATEGORY === category && value.CATEGORY_DESCRIPTION === label && value.METRIC_TITLE === chartMetric).sort(yearSort);

    }

    function getNotApproachingData(category, label) {
        let array = [];
        let approachingValues = getApproachingData(category, label);
        let rawData = getSnapshotRaw(category, label);

        // 'start' and 'end' values are needed for floating bars below zero.  Leftmost bar needs to know the width of other bars below 0.
        rawData.forEach((value, index) => {
            let approaching = approachingValues[index][0];

            if (value.LESSTHAN10 == 1 || value.NODATA == 1) {
                // this is a horrific hack to get "No Data" messages to show on the chart for now. 
                // it's paired with the code in the display callback of the chart
                array.push([NaN, NaN])
                return true;
            }

            if (value.NOTYETAPPROACHING_PERCENT !== "NULL") {
                let notApproaching = parseFloat(value.NOTYETAPPROACHING_PERCENT);
                let start = 0 - (Math.abs(approaching) + notApproaching);
                let end = 0 + approaching;
                array.push([start, end]);
            } else {
                array.push([approaching, approaching])
            }
        })
        return array;
    }

    function getApproachingData(category, label) {
        let array = [];
        let rawData = getSnapshotRaw(category, label);

        rawData.forEach((value) => {
            if (value.APPROACHING_PERCENT) {
                array.push([0 - parseFloat(value.APPROACHING_PERCENT), 0])
            } else {
                array.push([0, 0])
            }
        })
        return array;
    }

    function getExceedingData(category, label) {
        let array = [];
        let rawData = getSnapshotRaw(category, label);
        rawData.forEach((value) => {
            array.push(parseFloat(value.MEETSEXCEEDS_PERCENT))
        })
        return array;
    }

    function getNotApproachingTrendData(data, category, label) {
        let array = [];
        let approachingValues = getApproachingTrendData(data, category, label);
        let rawData = getTrendRaw(data, category, label);
        rawData.forEach((value, index) => {
            let approaching;
            let notApproaching;
            if (value.LESSTHAN10 == 1 || value.NODATA == 1) {
                // this is a horrific hack to get "No Data" messages to show on the chart for now. 
                // it's paired with the code in the display callback of the chart
                array.push([NaN, 0])
                return true;
            }
            if (!isNaN(parseFloat(approachingValues[index][0]))) {
                approaching = approachingValues[index][0];
            } else {
                approaching = 0;
            }
            if (!isNaN(parseFloat(value.NOTYETAPPROACHING_PERCENT))) {
                notApproaching = parseFloat(value.NOTYETAPPROACHING_PERCENT);
            } else {
                notApproaching = 0;
            }
            let start = 0 - (Math.abs(approaching) + notApproaching);
            let end = 0 + approaching;
            array.push([start, end]);
        })
        return array;
    }

    function getApproachingTrendData(data, category, label) {
        let array = [];
        let rawData = getTrendRaw(data, category, label);
        rawData.forEach((value) => {
            if (!isNaN(parseFloat(value.APPROACHING_PERCENT))) {
                array.push([0 - parseFloat(value.APPROACHING_PERCENT), 0])
            } else {
                array.push([value.APPROACHING_PERCENT, 0])
            }
        })
        return array
    }

    function getExceedingTrendData(data, category, label) {
        let array = [];
        let rawData = getTrendRaw(data, category, label);
        rawData.forEach((value) => {
            array.push(parseFloat(value.MEETSEXCEEDS_PERCENT))
        })
        return array;
    }

    function getTrendData(data, category, label) {
        const ret = {
            labels: trendLabels,
            datasets: [{
                label: 'Not Yet Approaching Benchmark',
                data: getNotApproachingTrendData(data, category, label),
                backgroundColor: '#FFA110',
            }, {
                label: 'Approaching Benchmark',
                data: getApproachingTrendData(data, category, label),
                backgroundColor: '#8B2BA1',
                datalabels: {
                    color: 'white'
                }
            }, {
                label: 'Exceeding Benchmark',
                data: getExceedingTrendData(data, category, label),
                backgroundColor: '#047A6E',
                datalabels: {
                    color: 'white'
                }
            }]
        }
        return ret;
    }

    function getDropoutSnapshotData(category, label) {
        return {
            labels: ['School', 'Network', 'District'],
            datasets: [{
                label: 'Not Yet Approaching Benchmark',
                data: getNotApproachingData(category, label),
                backgroundColor: '#FFA110',
            }, {
                label: 'Approaching Benchmark',
                data: getApproachingData(category, label),
                backgroundColor: '#8B2BA1',
                datalabels: {
                    color: 'white'
                }
            }, {
                label: 'Exceeding Benchmark',
                data: getExceedingData(category, label),
                backgroundColor: '#047A6E',
                datalabels: {
                    color: 'white'
                }
            }]
        }
    }

    function getAllSnapshotConfig(category, label) {
        return {
            type: 'bar',
            plugins: [legendPad, ChartDataLabels],
            data: getDropoutSnapshotData(category, label),
            options: {
                layout: {
                    padding: {
                        right: 50
                    }
                },
                indexAxis: 'y',
                maintainAspectRatio: false,
                onResize: onResize,
                scales: {
                    x: {
                        max: 100,
                        min: -100,
                        grid: {
                            display: false
                        },
                        grace: 1.1,
                        title: {
                            display: true,
                            text: "% of Students",
                            color: "black"
                        },
                    }, y: {
                        stacked: true,
                        grid: {
                            offset: false,
                            display: false
                        }, ticks: {
                            color: "black"
                        }
                    }
                }, plugins: {
                    datalabels: snapshotDatalabelConfig,
                    tooltip: snapshotTooltipConfig,
                    legend: { display: false },
                    htmlLegend: {
                        containerID: `${blockId}-complex-template-snapshot`
                    }
                }
            }
        }
    }

    function getCategoryTrendConfig(data, category, label) {
        return {
            type: 'bar',
            plugins: [ChartDataLabels],
            data: getTrendData(data, category, label),
            options: {
                layout: {
                    padding: {
                        right: 50
                    }
                },

                onResize: onResize,
                maintainAspectRatio: false,
                indexAxis: 'y',
                scales: {
                    x: {
                        grid: {
                            display: false
                        },
                        max: 100,
                        min: -100,
                        grace: 1.1,
                        title: {
                            display: true,
                            text: "% of Students",
                            color: "black"
                        }
                    }, y: {
                        stacked: true,
                        grid: {
                            offset: false,
                            display: false
                        }, ticks: {
                            color: "black"
                        }
                    }
                }, plugins: {
                    datalabels: trendDatalabelConfig,
                    tooltip: trendTooltipConfig,
                    legend: {display: false},                    
                    htmlLegend: {
                        containerID: generateChartId(category, `${blockId}-complex-block`)
                    }
                }
            }
        }
    }

    let absenceSelect = document.getElementById(scopedId("sat-math-demographics"));
    absenceSelect.addEventListener("change", changeDropoutDemographic);

    function setSnapshotHeight() {
        let height;
        let length = snapshotLabels[selectedCategory].length;
        if (selectedCategory === "Race/Ethnicity") {
            height = length * 415;
        } else {
            height = length * 400;
        }
        let snapshotHeight = document.getElementById(scopedId("sat-math-snapshot-height"));
        snapshotHeight.style.height = height.toString() + "px";
    }

    function changeDropoutDemographic(event) {
        let selected = event.target.value;

        selectedCategory = selected;
        setTrendChart();
        let $retired = $(`#${scopedId("sat-math-retired")}`);
        setSnapshotHeight();
        switch (selected) {
            case "Race/Ethnicity":
                $retired.removeClass("hidden");
                break;
            default:
                $retired.addClass("hidden");
                break;
        }

    }

    function createSnapshotTables() {
        let rows = [schoolName, "Network", "District"];
        let columns = ["NOTYETAPPROACHING", "APPROACHING", "MEETSEXCEEDS"]
        let categories = Object.keys(snapshotLabels);
        categories.forEach((category) => {
            let type;
            if (category === "Race/Ethnicity") {
                type = "race";
            } else {
                type = category.toLowerCase().replace(/ .*/, '');
            }

            let labels = snapshotLabels[category];
            let parent = document.getElementById(scopedId(`sat-math-${type}-tables`));

            labels.forEach((label) => {
                let data = getSnapshotRaw(category, label);
                let categoryChartTableDiv = document.createElement('div');
                categoryChartTableDiv.classList.add('snapshot-chart');
                let header = document.createElement('p');
                //const tipContent = bubbleConfigs[mappedBubbleConfigFieldKeys[`${category} - ${label}`]]?.DescriptionHtmlString ?? (notUseDefaultTooltip ? null : getTooltip(category));
                const tipContent = getCategoryMappedTooltip(category);
                header.innerHTML = `<p style="text-align: center;"><strong><span class="school-name">${config.schoolName} </span>- ${config.metricName} - <span>${bubbleConfigs[mappedBubbleConfigFieldKeys[`${category} - ${label}`]]?.Title ?? label}</span>&nbsp;<span data-toggle="school-profile-tooltip"
                                                                                 class="school-info-bubble fa fa-info-circle"
                                                                                 data-html="true"
                                                                                 data-tooltip-content="${tipContent}" aria-label="More Info: Click here"></span></strong>
                        </p>`;
                categoryChartTableDiv.appendChild(header);
                let categoryChartBox = document.createElement('div');
                categoryChartBox.classList.add("table-scrolling-wrapper");
                let categoryTable = document.createElement('table');
                categoryTable.classList.add('chart-table');
                let categoryThead = categoryTable.createTHead();
                categoryThead.insertRow(0);
                categoryThead.rows[0].insertCell(0).classList.add('light-cyan');

                categoryThead.rows[0].insertCell(1).outerHTML = `<th>Not Yet Approaching Benchmark</th>`;
                categoryThead.rows[0].insertCell(2).outerHTML = `<th>Approaching Benchmark</th>`;
                categoryThead.rows[0].insertCell(3).outerHTML = `<th>Meet/Exceed Benchmark</th>`;

                const categoryTableBody = categoryTable.createTBody();
                data.forEach((value, index) => {
                    categoryTableBody.insertRow(index);
                    categoryTableBody.rows[index].insertCell(0).outerHTML = `<th scope='row'>${rows[index]}</th>`;
                    columns.forEach((column, i) => {
                        if (value.LESSTHAN10 == 1) {
                            categoryTableBody.rows[index].insertCell(i + 1).innerText = "Fewer than 10 students";
                        } else if (value.NODATA == 1) {
                            categoryTableBody.rows[index].insertCell(i + 1).innerText = "No data";
                        } else {
                            categoryTableBody.rows[index].insertCell(i + 1).innerText = `${value[columns[i] + '_PERCENT'].toLocaleString()}%\n${Number(value[columns[i] + '_NUMERATOR']).toLocaleString()}`;
                        }
                    })
                })
                categoryChartBox.appendChild(categoryTable);
                categoryChartTableDiv.appendChild(categoryChartBox)
                parent.appendChild(categoryChartTableDiv)
                
                header.querySelectorAll<HTMLElement>('[data-toggle="school-profile-tooltip"]').forEach((el) => {
                    infoBubble(el);
                });                
            })

        })
    }

    function createTrendTables(data) {

        for (const category in snapshotLabels) {
            let columns = ["NOTYETAPPROACHING", "APPROACHING", "MEETSEXCEEDS"]
            let type;
            if (category === "Race/Ethnicity") {
                type = "race";
            } else {
                type = category.toLowerCase().replace(/ .*/, '');
            }
            let parent = document.getElementById(scopedId(`sat-math-${type}-tables-${data}-trend`));
            snapshotLabels[category].forEach((label) => {
                let dataset = getTrendRaw(data, category, label);
                let categoryChartTableDiv = document.createElement('div');
                categoryChartTableDiv.classList.add('snapshot-chart');
                let header = document.createElement('p');
                let title;
                switch (data) {
                    case "school":
                        title = schoolName;
                        break;
                    case "network":
                        title = network
                        break;
                    case "district":
                        title = "District";
                        break;

                }
                const tipContent = getCategoryMappedTooltip(category);
                header.innerHTML = `<p style="text-align: center;"><strong><span class="school-name">${config.schoolName} </span>- ${config.metricName} - <span>${bubbleConfigs[mappedBubbleConfigFieldKeys[`${category} - ${label}`]]?.Title ?? label}</span>&nbsp;<span data-toggle="school-profile-tooltip"
                                                                                 class="school-info-bubble fa fa-info-circle"
                                                                                 data-html="true"
                                                                                 data-tooltip-content="${tipContent}" aria-label="More Info: Click here"></span></strong>
                        </p>`;                                 
                categoryChartTableDiv.appendChild(header);
                let categoryChartBox = document.createElement('div');
                categoryChartBox.classList.add("table-scrolling-wrapper");
                let categoryTable = document.createElement('table');
                categoryTable.classList.add('chart-table');
                let categoryThead = categoryTable.createTHead();
                categoryThead.insertRow(0);
                categoryThead.rows[0].insertCell(0).classList.add('light-cyan');

                categoryThead.rows[0].insertCell(1).outerHTML = `<th>Not Yet Approaching Benchmark</th>`;
                categoryThead.rows[0].insertCell(2).outerHTML = `<th>Approaching Benchmark</th>`;
                categoryThead.rows[0].insertCell(3).outerHTML = `<th>Meet/Exceed Benchmark</th>`;

                const categoryTableBody = categoryTable.createTBody();
                dataset.forEach((value, index) => {
                    categoryTableBody.insertRow(index);
                    categoryTableBody.rows[index].insertCell(0).outerHTML = `<th scope='row'>${trendLabels[index]}</th>`;
                    columns.forEach((column, i) => {
                        if (value.LESSTHAN10 == 1) {
                            categoryTableBody.rows[index].insertCell(i + 1).innerText = "Fewer than 10 students";
                        } else if (value.NODATA == 1) {
                            categoryTableBody.rows[index].insertCell(i + 1).innerText = "No data";
                        } else {
                            categoryTableBody.rows[index].insertCell(i + 1).innerText = `${value[columns[i] + '_PERCENT'].toLocaleString()}%\n${Number(value[columns[i] + '_NUMERATOR']).toLocaleString()}`;
                        }
                    })
                })
                categoryChartBox.appendChild(categoryTable);
                categoryChartTableDiv.appendChild(categoryChartBox)
                parent.appendChild(categoryChartTableDiv)
                header.querySelectorAll<HTMLElement>('[data-toggle="school-profile-tooltip"]').forEach((el) => {
                    infoBubble(el);
                }); 
                
            })
        }
    }

    function createSnapshot() {
        let categories = Object.keys(snapshotLabels);
        categories.forEach((category) => {
            let type;
            if (category === "Race/Ethnicity") {
                type = "race";
            } else {
                type = category.toLowerCase().replace(/ .*/, '');
            }

            let labels = snapshotLabels[category];
            let parent = document.getElementById(scopedId(`sat-math-${type}-charts`));                
            labels.forEach((label) => {
                let container = document.createElement("div");
                container.classList.add("snapshot-container") 
                const tipContent = getCategoryMappedTooltip(category);
                container.innerHTML = `<p style="text-align: center;"><strong><span class="school-name">${config.schoolName} </span>- ${config.metricName} - <span>${bubbleConfigs[mappedBubbleConfigFieldKeys[`${category} - ${label}`]]?.Title ?? label}</span>&nbsp;<span data-toggle="school-profile-tooltip"
                                                                                 class="school-info-bubble fa fa-info-circle"
                                                                                 data-html="true"
                                                                                 data-tooltip-content="${tipContent}" aria-label="More Info: Click here"></span></strong>
                        </p>`;

                let canvasDiv = document.createElement('canvas');
                canvasDiv.setAttribute('id', scopedId(`snapshot_sat-math_${label}_${category}`));
                container.appendChild(canvasDiv);
                parent.appendChild(container);

                new Chart(
                    // @ts-ignore
                    document.getElementById(scopedId(`snapshot_sat-math_${label}_${category}`)),
                    getAllSnapshotConfig(category, label)
                );
                container.querySelectorAll<HTMLElement>('[data-toggle="school-profile-tooltip"]').forEach((el) => {
                    infoBubble(el);
                });               
            })
        })

        createSnapshotTables();
    }
    function buildTrends() {

        ["school", "network", "district"].forEach((dataset) => {
            createTrends(dataset);
            createTrendTables(dataset);
        })
    }

    function createTrends(dataset) {
        return new Promise((resolve) => {
            const trends = [
                { id: `sat-math-trend_${dataset}_All Students_ALL`, category: 'ALL', group: 'All Students' },
                { id: `sat-math-trend_${dataset}_Economic Disadvantage_Economic Disadvantage`, category: 'Economic Disadvantage', group: 'Economic Disadvantage' },
                { id: `sat-math-trend_${dataset}_Non-Economic Disadvantage_Economic Disadvantage`, category: 'Economic Disadvantage', group: 'Non-Economic Disadvantage' },
                { id: `sat-math-trend_${dataset}_English Learner_English Learner Status`, category: 'English Learner Status', group: 'English Learner' },
                { id: `sat-math-trend_${dataset}_Non-English Learner_English Learner Status`, category: 'English Learner Status', group: 'Non-English Learner' },
                { id: `sat-math-trend_${dataset}_Female_Gender`, category: 'Gender', group: 'Female' },
                { id: `sat-math-trend_${dataset}_Male_Gender`, category: 'Gender', group: 'Male' },
                { id: `sat-math-trend_${dataset}_Non-Binary_Gender`, category: 'Gender', group: 'Non-Binary' },
                { id: `sat-math-trend_${dataset}_IEP_IEP Status`, category: 'IEP Status', group: 'IEP' },
                { id: `sat-math-trend_${dataset}_Non-IEP_IEP Status`, category: 'IEP Status', group: 'Non-IEP' },
                { id: `sat-math-trend_${dataset}_Asian_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Asian' },
                { id: `sat-math-trend_${dataset}_Asian/Pacific Islander (Retired Category)_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Asian/Pacific Islander (Retired Category)' },
                { id: `sat-math-trend_${dataset}_Black/African American_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Black/African American' },
                { id: `sat-math-trend_${dataset}_Hawaiian/Pacific Islander_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Hawaiian/Pacific Islander' },
                { id: `sat-math-trend_${dataset}_Latinx_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Latinx' },
                { id: `sat-math-trend_${dataset}_Multiracial_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Multiracial' },
                { id: `sat-math-trend_${dataset}_Native American_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Native American' },
                { id: `sat-math-trend_${dataset}_Not Listed_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'Not Listed' },
                { id: `sat-math-trend_${dataset}_White_Race/Ethnicity`, category: 'Race/Ethnicity', group: 'White' },
                { id: `sat-math-trend_${dataset}_Non-Temporary Living Situation_Temporary Living Situation Status`, category: 'Temporary Living Situation Status', group: 'Non-Temporary Living Situation' },
                { id: `sat-math-trend_${dataset}_Temporary Living Situation_Temporary Living Situation Status`, category: 'Temporary Living Situation Status', group: 'Temporary Living Situation' },
            ];
            for (let trend of trends) {
                // @ts-ignore
                new Chart(document.getElementById(scopedId(trend.id)), getCategoryTrendConfig(dataset, trend.category, trend.group))
            }
            resolve(true);
            
        })
    }

    $(document).ready(function () {
        buildTrends();
        showTrends({
                target: {
                    value: 'school'
                }
            });
    });

    function showSnapshot() {
        // $("#show-trends, #show-network, #show-district").removeClass("active");
        $('#'+scopedId('sat-math-trend-select')).removeClass("active");
        $(parentScope).find(".sat-math-school-trends, .sat-math-network-trends, .sat-math-district-trends").hide();
        $(parentScope).find(".sat-math-snapshot").show();
        $('#' + scopedId("sat-math-show-snapshot")).addClass("active");
    }

    function showTrends(event) {
        let selected = event.target.value;
        $('#' + scopedId("sat-math-show-snapshot")).removeClass("active");
        $(parentScope).find(".sat-math-snapshot").hide();
        $('#' + scopedId('sat-math-trend-select')).addClass("active");
        $(parentScope).find(".sat-math-school-trends, .sat-math-network-trends, .sat-math-district-trends").hide();
        $(parentScope).find('.sat-math-' + selected + '-trends').show();
    }

    function displayTrend(value) {
        $('#' + scopedId("sat-math-show-snapshot")).removeClass("active");
        $(parentScope).find(".sat-math-snapshot").hide();
        $('#' + scopedId('sat-math-trend-select')).addClass("active");
        $(parentScope).find('.sat-math-' + value + '-trends').show();
    }

    function hideTrendCharts() {
        $(parentScope).find("div.sat-math-all, div.sat-math-economic,div.sat-math-english,div.sat-math-gender,div.sat-math-iep,div.sat-math-race,div.sat-math-temporary").hide();
    }

    function setTrendChart() {
        hideTrendCharts();
        switch (selectedCategory) {
            case "ALL":
                $(parentScope).find("div.sat-math-all").show();
                break;
            case "Race/Ethnicity":
                $(parentScope).find("div.sat-math-race").show();
                break;
            case "Economic Disadvantage":
                $(parentScope).find("div.sat-math-economic").show();
                break;
            case "English Learner Status":
                $(parentScope).find("div.sat-math-english").show();
                break;
            case "Gender":
                $(parentScope).find("div.sat-math-gender").show();
                break;
            case "IEP Status":
                $(parentScope).find("div.sat-math-iep").show();
                break;
            case "Temporary Living Situation Status":
                $(parentScope).find("div.sat-math-temporary").show();
                break;
        }
    }
});